import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Container, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from './app/store';
import InProgressBar from './components/InProgressBar';
import Loading from './components/Loading';
import Menu from './components/Menu';
import Navbar from './components/Navbar';
import NormalRoute from './components/NormalRoute';
import PrivateRoute from './components/PrivateRoute';
import ScrollTop from './components/ScrollTopButton';
import { useAppDispatch } from './hooks/useAppRudux';
import Account, { getDateFormatFromStorage } from './pages/Account';
import AlertBox from './pages/Alert/AlertBox';
import {
  autoSignInRedirect,
  forceSignOut,
  getActiveAccount,
  getToken,
  redirectAfterLogin,
  signOut
} from './pages/Auth/authSCG';
import Dashboard from './pages/Dashboard';
import ProjectDashboard from './pages/Dashboard/ProjectDashboard';
import DeleteDocumentView from './pages/DeleteProjectDocument/DeleteDocumentView';
import Error403 from './pages/Error/403';
import Error404 from './pages/Error/404';
import ComingSoon from './pages/Error/ComingSoon';
import Help from './pages/Help';
import Home from './pages/Home';
import loadingSlice from './pages/Loading/loadingSlice';
import Login from './pages/Login';
import MasterData from './pages/MasterData';
import { getAccessControlsAsync } from './pages/MasterData/AccessControl/accessControlSlice';
import { getContractorsAsync } from './pages/MasterData/Contractor/contractorSlice';
import { getDisciplinesAsync } from './pages/MasterData/Discipline/disciplineSlice';
import { getDocumentsAsync } from './pages/MasterData/Document/documentSlice';
import { getPurposesAsync } from './pages/MasterData/Purpose/purposeSlice';
import { getRevisionsAsync } from './pages/MasterData/Revision/revisionSlice';
import StandardWorkflow from './pages/MasterData/StandardWorkflow';
import { getWorkflowStepsAsync } from './pages/MasterData/WorkflowStep/workflowStepSlice';
import MyTask from './pages/MyTask';
import Project from './pages/Project';
import ProjectCreate from './pages/Project/ProjectCreate';
import ProjectEdit from './pages/Project/ProjectEdit';
import { getUsersInProjectRegisterAsync } from './pages/Project/projectRegisterSlice';
import { plugAndPlayConfig } from './config/plugAndPlay';
import { getProjectUsersAsync } from './pages/Project/projectUsersSlice';
import ProjectView from './pages/Project/ProjectView';
import ProjectAccessControlUserEdit from './pages/ProjectAccessControlUser/ProjectAccessControlUserEdit';
import BulkPlanning from './pages/ProjectDocument/BulkPlanning';
import DocumentCreate from './pages/ProjectDocument/DocumentCreate';
import DocumentEdit from './pages/ProjectDocument/DocumentEdit';
import DocumentView from './pages/ProjectDocument/DocumentView';
import DocumentShare from './pages/ProjectDocumentCustomAccessControl/DocumentShare';
import DocumentRevisionCreate from './pages/ProjectDocumentRevision/DocumentRevisionCreate';
import DocumentRevisionEdit from './pages/ProjectDocumentRevision/DocumentRevisionEdit';
import DocumentRevisionView from './pages/ProjectDocumentRevision/DocumentRevisionView';
import DocumentFileView from './pages/ProjectDocumentRevisionFile/ProjectDocumentRevisionFileView';
import DocumentVersionFileView from './pages/ProjectDocumentVersionFile/ProjectDocumentVersionFileView';
import DocumentWorkflowApproval from './pages/ProjectDocumentWorkflow/ProjectDocumentWorkflowApproval';
import DocumentWorkflowCreate from './pages/ProjectDocumentWorkflow/ProjectDocumentWorkflowCreate';
import DocumentWorkflowEdit from './pages/ProjectDocumentWorkflow/ProjectDocumentWorkflowEdit';
import WorkflowCreate from './pages/ProjectWorkflow/ProjectWorkflowCreate';
import WorkflowEdit from './pages/ProjectWorkflow/ProjectWorkflowEdit';
import Search from './pages/Search';
import { verifyScgUserAsync } from './pages/UserProfile/userProfileSlice';
import DocumentMasterList from './pages/DocumentMasterList';
import PlugAndPlayProjectCreate from './pages/Project/PlugAndPlayProjectCreate';
import { get } from 'http';

let isForceSignOut = false;

export default function App() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const token = getToken();
  const activeAccountKey = getActiveAccount();
  const { instance, inProgress, accounts } = useMsal();
  const { exp, user } = useSelector((state: RootState) => state.userProfile);
  const { code, isLoading, showLoading } = useSelector(
    (state: RootState) => state.loading
  );

  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  // useRef to store the previous projectRegisterId across renders
  const prevProjectRegisterIdRef = useRef<number | null>(null);

  const accessTokenRequest = {
    scopes: ['user.read'],
    account: accounts[0]
  };
  const isLoggedIn = Boolean(user);
  const isExpired = exp ? exp < Date.now() / 1000 : false;

  useEffect(() => {
    // Get Access token from Azure
    async function authenticate() {
      try {
        await dispatch(
          verifyScgUserAsync({ accessTokenRequest, instance })
        ).unwrap();
        // Clear error that came from token not found and error information still stored in the state
        dispatch(loadingSlice.actions.clear());
        redirectAfterLogin(history);
        isForceSignOut = false;
      } catch (error) {
        if (error instanceof Error)
          loadingSlice.actions.error({ message: error.message });
        setTimeout(() => signOut({ dispatch, instance }), 2000); // Delay timer for displaying the error
      }
    }

    // Checking conditions before logging in with SCG Azure
    // Skip Azure authentication if Plug and Play mode is enabled
    if (
      !plugAndPlayConfig.isEnabled &&
      !token &&
      inProgress === InteractionStatus.None
    ) {
      if (accessTokenRequest.account) {
        authenticate();
        getDateFormatFromStorage(); // Load date format from storage
      } else if (activeAccountKey) {
        autoSignInRedirect(instance, activeAccountKey);
      }
    }
  }, [accounts, inProgress, isForceSignOut]);

  useEffect(() => {
    // Only dispatch if:
    // 1. selectedProject exists
    // 2. For Legacy mode: selectedProject.projectRegisterId is different from the previous one
    // 3. For Plug and Play mode: selectedProject exists (no projectRegisterId check needed)
    if (selectedProject) {
      if (plugAndPlayConfig.isEnabled) {
        // Plug and Play mode: Load project-specific users
        dispatch(getProjectUsersAsync(selectedProject.id));
      } else {
        // Legacy mode: Only load if projectRegisterId is different from the previous one
        if (
          selectedProject.projectRegisterId !== prevProjectRegisterIdRef.current
        ) {
          // Update the ref to the new projectRegisterId
          prevProjectRegisterIdRef.current = selectedProject.projectRegisterId;
          dispatch(
            getUsersInProjectRegisterAsync(selectedProject.projectRegisterId)
          );
        }
      }
    }
  }, [selectedProject, dispatch]);

  useEffect(() => {
    function fetchFieldData() {
      dispatch(getAccessControlsAsync());
      dispatch(getContractorsAsync());
      dispatch(getDisciplinesAsync());
      dispatch(getDocumentsAsync());
      dispatch(getPurposesAsync());
      dispatch(getRevisionsAsync());
      dispatch(getWorkflowStepsAsync());
    }
    if (user) {
      fetchFieldData();
    }
  }, [user]);

  useEffect(() => {
    if (code) dispatch(loadingSlice.actions.clearCode());
  }, [code]);

  function handleDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenDrawer(open);
    };
  }

  if (isExpired) {
    if (user && !user.isExternalUser) {
      forceSignOut(dispatch, 'azure-scg');
    } else {
      forceSignOut(dispatch);
    }
    isForceSignOut = true; // To call useEffect for re-authorizing
  }

  // Use for preventing render app
  if (!token && inProgress !== InteractionStatus.None) {
    let text = '';

    switch (inProgress) {
      case 'login':
        text = t('app.takingToLogin');
        break;
      case 'handleRedirect':
        text = t('app.retrievingData');
        break;
      case 'logout':
        text = t('app.takingToLogout');
        break;
      default:
        text = t('app.loading');
        break;
    }

    return <Loading text={text} />;
  }

  if (!token && accessTokenRequest.account) {
    return (
      <>
        {/* To display error if cannot find the user in backspace's database */}
        {!isForceSignOut && <AlertWithInformation />}
        <Loading text={t('app.retrievingUserData')} />
      </>
    );
  }

  if (code === 403) {
    return <Redirect to="/error/403" />;
  }
  if (code === 404) {
    return <Redirect to="/error/404" />;
  }

  return (
    <div>
      <AlertWithInformation />
      {isLoggedIn && (
        <>
          {(isLoading || showLoading) && <InProgressBar />}
          <Navbar handleDrawer={handleDrawer} />
          <Menu openDrawer={openDrawer} onCloseDrawer={handleDrawer(false)} />
        </>
      )}
      <Container id="container" className={classes.container}>
        <Switch>
          <PrivateRoute exact path="/home" component={Home} />
          <PrivateRoute
            exact
            path="/my-tasks"
            permission="documentManagement.myTask.read"
            component={MyTask}
          />
          <PrivateRoute
            exact
            path="/projects"
            permission="documentManagement.project.readAll"
            component={Project}
          />
          <PrivateRoute
            exact
            path="/projects/create"
            permission="documentManagement.project.create"
            component={
              plugAndPlayConfig.isEnabled
                ? PlugAndPlayProjectCreate
                : ProjectCreate
            }
          />
          <PrivateRoute
            exact
            path="/projects/:projectId"
            permission="documentManagement.project.read"
            component={ProjectView}
          />
          <PrivateRoute
            exact
            path="/projects/:projectId/edit"
            permission="documentManagement.projectSetting.update"
            component={ProjectEdit}
          />
          <PrivateRoute
            exact
            path="/projects/:projectId/members"
            permission="documentManagement.projectSetting.update"
            component={ProjectAccessControlUserEdit}
          />
          <PrivateRoute
            exact
            path="/projects/:projectId/dashboards"
            permission="documentManagement.projectDashboard.read"
            component={ProjectDashboard}
          />
          <PrivateRoute
            exact
            path="/projects/:projectId/history"
            permission="documentManagement.projectDocument.readAll"
            component={DeleteDocumentView}
          />
          <PrivateRoute
            exact
            path="/documents/create"
            permission="documentManagement.projectDocument.create"
            component={DocumentCreate}
          />
          <PrivateRoute
            path="/documents/bulk-planning"
            permission="documentManagement.projectDocument.create"
            component={BulkPlanning}
          />
          <PrivateRoute
            exact
            path="/documents/:docId"
            permission="documentManagement.projectDocument.read"
            component={DocumentView}
          />
          <PrivateRoute
            exact
            path="/documents/:docId/edit"
            permission="documentManagement.projectDocument.update"
            component={DocumentEdit}
          />
          <PrivateRoute
            exact
            path="/documents/:docId/share"
            permission="documentManagement.projectDocument.update"
            component={DocumentShare}
          />
          <PrivateRoute
            exact
            path="/document-revisions/create"
            permission="documentManagement.projectDocument.create"
            component={DocumentRevisionCreate}
          />
          <PrivateRoute
            exact
            path="/document-revisions/:revId"
            permission="documentManagement.projectDocument.read"
            component={DocumentRevisionView}
          />
          <PrivateRoute
            exact
            path="/document-revisions/:revId/edit"
            permission="documentManagement.projectDocument.update"
            component={DocumentRevisionEdit}
          />
          <PrivateRoute
            exact
            path="/document-files/:fileId"
            permission="documentManagement.projectDocument.read"
            component={DocumentFileView}
          />
          <PrivateRoute
            exact
            path="/document-versions/:versionId"
            component={DocumentVersionFileView}
          />
          <PrivateRoute
            exact
            path="/project-workflows/create"
            permission="documentManagement.projectWorkflow.create"
            component={WorkflowCreate}
          />
          <PrivateRoute
            exact
            path="/project-workflows/:flowId/edit"
            permission="documentManagement.projectWorkflow.update"
            component={WorkflowEdit}
          />
          <PrivateRoute
            exact
            path="/document-workflows/create"
            permission="documentManagement.projectDocument.create"
            component={DocumentWorkflowCreate}
          />
          <PrivateRoute
            exact
            path="/document-workflows/:docFlowId/edit"
            permission="documentManagement.projectDocument.update"
            component={DocumentWorkflowEdit}
          />
          <PrivateRoute
            exact
            path="/approvals/:docFlowId"
            permission="documentManagement.userApproval.read"
            component={DocumentWorkflowApproval}
          />
          <PrivateRoute
            exact
            path="/dashboards"
            permission="documentManagement.dashboard.read"
            component={Dashboard}
          />
          <PrivateRoute
            exact
            path="/standard-workflows"
            component={StandardWorkflow}
          />
          <PrivateRoute
            exact
            path="/document-master-list"
            component={DocumentMasterList}
          />
          <PrivateRoute
            path="/master-data"
            permission="documentManagement.masterData.access"
            component={MasterData}
          />
          <PrivateRoute path="/helps" component={Help} />
          <PrivateRoute exact path="/search" component={Search} />
          <PrivateRoute path="/account" component={Account} />
          <NormalRoute exact path="/login" component={Login} />
          <Route exact path="/error/403" component={Error403} />
          <Route exact path="/error/404" component={Error404} />
          <Route exact path="/error/coming-soon" component={ComingSoon} />
          <Route exact path="/" component={() => <Redirect to="/home" />} />
          <Route
            exact
            path="*"
            component={() => <Redirect to="/error/404" />}
          />
        </Switch>
      </Container>
      <ScrollTop showBelow={250} />
    </div>
  );
}

function AlertWithInformation(): JSX.Element {
  const dispatch = useAppDispatch();
  const { openAlert, duration, status, message } = useSelector(
    (state: RootState) => state.loading
  );

  return (
    <AlertBox
      open={openAlert}
      onClose={() => dispatch(loadingSlice.actions.clear())}
      autoHideDuration={duration}
      severity={status}
    >
      {message}
    </AlertBox>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(9.5),
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(8.5)
      }
    }
  })
);
